/**
 * Lời gọi API cho phân hệ bảng tin điện tử công cộng (MH-24 thuộc tính bảng),
 * điểm cuối `/api/v1/digital-signage/devices`. Bản tin và lịch bảng tin ở
 * `bang-tin-api.ts`. Cùng khuôn phong bì và xử lý lỗi với `thiet-bi-api.ts`.
 */
import { layToken } from './khach-api';
import type { PhongBiNoiBo } from './khach-api';

export class LoiApiBangDienTu extends Error {
  constructor(
    readonly ma: string,
    thongBao: string,
    readonly requestId?: string,
  ) {
    super(thongBao);
    this.name = 'LoiApiBangDienTu';
  }
}

function goc(): string {
  return (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? '';
}

async function goi<T>(
  duongDan: string,
  tuyChon: { phuongThuc?: string; than?: unknown; signal?: AbortSignal; formData?: FormData } = {},
): Promise<T> {
  const token = layToken();
  const res = await fetch(`${goc()}${duongDan}`, {
    method: tuyChon.phuongThuc ?? 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(tuyChon.than !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    body: tuyChon.formData ?? (tuyChon.than !== undefined ? JSON.stringify(tuyChon.than) : undefined),
    signal: tuyChon.signal,
  });
  let pb: PhongBiNoiBo<T>;
  try {
    pb = (await res.json()) as PhongBiNoiBo<T>;
  } catch {
    throw new LoiApiBangDienTu('INTERNAL_ERROR', 'Máy chủ trả về dữ liệu không đọc được');
  }
  if (!pb.success) throw new LoiApiBangDienTu(pb.code, pb.message, pb.requestId);
  return pb.data as T;
}

/* --------------------------- MH-24: thuộc tính bảng tin -------------------------- */

export interface ThuocTinhBangTin {
  deviceId: string;
  cumLoaId: string;
  tenThietBi: string | null;
  tenNhaCungCap: string;
  phuongThucKetNoi: string;
  doPhanGiai: string | null;
  khoangCachDiemAnhMm: number | null;
  dienTichM2: number | null;
  soVungPhat: number;
  trongNha: boolean;
  capBaoVeVoNgoai: string | null;
  boDieuKhien: string | null;
  huongManHinh: 'ngang' | 'doc' | null;
  chieuRongPx: number | null;
  chieuCaoPx: number | null;
  anhChupGanNhatUrl: string | null;
  anhChupGanNhatLuc: string | null;
}

export const bangDienTuApi = {
  /* MH-24 */
  thuocTinhChiTiet: (id: string, signal?: AbortSignal): Promise<ThuocTinhBangTin> =>
    goi(`/api/v1/digital-signage/devices/${id}/attributes`, { signal }),

  thuocTinhCapNhat: (id: string, than: Record<string, unknown>): Promise<ThuocTinhBangTin> =>
    goi(`/api/v1/digital-signage/devices/${id}/attributes`, { phuongThuc: 'PUT', than }),

  anhChupTaiLen: (id: string, file: File): Promise<{ khoa: string }> => {
    const fd = new FormData();
    fd.append('file', file);
    return goi(`/api/v1/digital-signage/devices/${id}/screenshot`, { phuongThuc: 'POST', formData: fd });
  },
};
